import React, { useState, createContext } from "react"
import type { ReactNode } from "react"
import cx from "classnames"

const HORIZONTAL_SIZES = {
  sm: "text-xs h-8 rounded-sm",
  md: "text-sm h-10 rounded",
  lg: "text-sm h-12 rounded-lg"
}

const VERTICAL_SIZES = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm"
}

type OldButtonGroupContextValue = {
  present?: boolean
  groupSize?: string
  activeId?: string | null
  setActiveId?: React.Dispatch<React.SetStateAction<string | null>>
  orientation?: "horizontal" | "vertical"
}

export const OldButtonGroupContext = createContext<OldButtonGroupContextValue>(
  {}
)

type OldButtonGroupOwnProps = {
  /** Gap and padding size between buttons */
  spacing?: number | string
  /** Layout direction of the group */
  orientation?: "horizontal" | "vertical"
  /** Size of the button group */
  size?: keyof typeof HORIZONTAL_SIZES
  /** Additional CSS classes */
  className?: string
  /** Button elements */
  children?: ReactNode
}

type OldButtonGroupProps = OldButtonGroupOwnProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof OldButtonGroupOwnProps>

function OldButtonGroup({
  children,
  className,
  spacing = 1,
  orientation = "horizontal",
  size = "md",
  ...props
}: OldButtonGroupProps) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const classes = cx(
    "inline-grid auto-cols-fr auto-rows-fr items-center rounded overflow-hidden bg-gray-900/[0.06]",
    className,
    `gap-${spacing} p-${spacing}`,
    {
      "grid-flow-col": orientation === "horizontal",
      "grid-flow-row": orientation === "vertical",
      [HORIZONTAL_SIZES[size]]: orientation === "horizontal",
      [VERTICAL_SIZES[size]]: orientation === "vertical"
    }
  )

  const contextValue: OldButtonGroupContextValue = {
    present: true,
    groupSize: `${orientation}-${size}`,
    activeId,
    setActiveId,
    orientation
  }

  return (
    <div {...props} className={classes} data-testid="btn-group" role="group">
      <OldButtonGroupContext.Provider value={contextValue}>
        {children}
      </OldButtonGroupContext.Provider>
    </div>
  )
}

export { OldButtonGroup, OldButtonGroup as ButtonGroup }
